import { Box, Stack, Tooltip, Typography } from "@mui/material";
import { ChevronRight } from "@mui/icons-material";
import { alpha, useTheme } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";
import mapRouteSegment from "../../../utils/easyMaps/mapRouteSegment";
import { timeAgo } from "../../../pages/homeIn/components/utils";

const TIPO_LABEL = ["GEO", "TOP", "POP", "FTTH", "PING"];
const SEVERITY = {
  5: { key: "disaster", label: "Desastre" },
  4: { key: "high", label: "Alto" },
  3: { key: "average", label: "Médio" },
  2: { key: "warning", label: "Atenção" },
};

function tipoColor(theme, tipo) {
  return (
    [
      theme.palette.primary.main,
      theme.palette.secondary.main,
      theme.palette.color.zabbixSeverty.warning,
      theme.palette.info.main,
    ][tipo] ?? theme.palette.color.alerts.success
  );
}

function ElementoTooltip({ elemento, sev, theme }) {
  const cor = sev
    ? theme.palette.color.zabbixSeverty[sev.key]
    : alpha(theme.palette.secondary.main, 0.7);
  return (
    <Box sx={{ py: 0.5, minWidth: 140 }}>
      <Typography sx={{ fontSize: 13, fontWeight: 600, mb: 0.5 }}>
        {elemento.titulo}
      </Typography>
      <Stack direction="row" alignItems="center" gap={0.75}>
        <Box sx={{ width: 8, height: 8, borderRadius: "2px", bgcolor: cor }} />
        <Typography sx={{ fontSize: 12 }}>
          {sev
            ? `${elemento.alertas} ${elemento.alertas === 1 ? "alerta" : "alertas"} · ${sev.label}`
            : "Sem alertas"}
        </Typography>
      </Stack>
    </Box>
  );
}

// linha da aba "Mapas recentes": um quadradinho por elemento do mapa,
// na cor da pior severidade do Zabbix entre os hosts do elemento
export default function RowMapRecente({
  id,
  mapName,
  tipo = 0,
  elementos = [],
  lastOpenedAt,
}) {
  const theme = useTheme();
  const navigate = useNavigate();
  const url = `/easyMaps/mapa/${mapRouteSegment(tipo)}/${id}`;
  const color = tipoColor(theme, tipo);
  const totalAlertas = elementos.reduce((acc, e) => acc + (e.alertas || 0), 0);

  return (
    <Stack
      direction="row"
      alignItems="center"
      gap={2}
      role="link"
      tabIndex={0}
      aria-label={`Abrir mapa ${mapName}`}
      sx={{
        px: 1.5,
        py: 1.25,
        cursor: "pointer",
        borderBottom: `1px solid ${theme.palette.divider}`,
        "&:last-of-type": { borderBottom: "none" },
        transition: "background-color .15s ease",
        "& .row-title": { transition: "color .15s ease" },
        "& .row-chevron": {
          color: "text.disabled",
          transition: "transform .15s ease, color .15s ease",
        },
        "&:hover, &:focus-visible": {
          backgroundColor: alpha(color, 0.08),
          "& .row-title": { color },
          "& .row-chevron": { color, transform: "translateX(3px)" },
        },
        "&:focus-visible": {
          outline: `2px solid ${color}`,
          outlineOffset: -2,
        },
      }}
      onClick={() => navigate(url)}
      onKeyDown={(e) => {
        if (e.key === "Enter") navigate(url);
      }}
      onMouseDown={(e) => {
        if (e.button === 1) window.open(window.location.origin + url, "_blank");
      }}
    >
      <Box
        sx={{
          width: 44,
          flexShrink: 0,
          py: 0.25,
          borderRadius: 1,
          textAlign: "center",
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: 0.5,
          color,
          backgroundColor: alpha(color, 0.12),
        }}
      >
        {TIPO_LABEL[tipo] ?? ""}
      </Box>

      <Box sx={{ minWidth: 0, width: { xs: 140, md: 260 }, flexShrink: 0 }}>
        <Typography
          className="row-title"
          noWrap
          sx={{ fontSize: 14, fontWeight: 500 }}
        >
          {mapName}
        </Typography>
        <Typography noWrap sx={{ fontSize: 12, color: "text.secondary" }}>
          {elementos.length} elementos
          {totalAlertas > 0 &&
            ` · ${totalAlertas} ${totalAlertas === 1 ? "alerta" : "alertas"}`}
          {lastOpenedAt && ` · ${timeAgo(lastOpenedAt)}`}
        </Typography>
      </Box>

      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: "3px",
          flexGrow: 1,
          minWidth: 0,
          justifyContent: "flex-end",
        }}
      >
        {elementos.map((elemento, index) => {
          const sev = SEVERITY[elemento.severity];
          return (
            <Tooltip
              key={index}
              arrow
              title={
                <ElementoTooltip elemento={elemento} sev={sev} theme={theme} />
              }
            >
              <Box
                sx={{
                  width: 10,
                  height: 10,
                  borderRadius: "2px",
                  transition: "transform .1s ease",
                  "&:hover": { transform: "scale(1.4)" },
                  bgcolor: sev
                    ? theme.palette.color.zabbixSeverty[sev.key]
                    : alpha(theme.palette.secondary.main, 0.7),
                }}
              />
            </Tooltip>
          );
        })}
      </Box>
      <ChevronRight
        className="row-chevron"
        fontSize="small"
        sx={{ flexShrink: 0 }}
      />
    </Stack>
  );
}
